import { FC, useState, useRef, CSSProperties } from "react";
import { alpha } from "@mui/material/styles";
import { Popover, styled, useTheme } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { FormattedMessage } from "react-intl";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { copyToClipboard, debounceFunc, toastError } from "../../../utils/AppUtils";

const PopoverWrapper = styled("div")(() => ({
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0 .5rem",
}));

type StyledSpanProps = {
    rightOffset?: CSSProperties["right"]; // Use the 'right' property type
};

const StyledSpan = styled("span")<StyledSpanProps>(({ theme, rightOffset }) => ({
    position: "relative",
    // cursor: "default", // Text is not clickable
    "& .copy-icon": {
        position: "absolute",
        right: rightOffset,
        top: "50%",
        transform: "translateY(-50%)",
        opacity: 0,
        visibility: "hidden",
        transition: "opacity 0.2s ease, visibility 0.2s ease",
        cursor: "pointer",
        fontSize: "1.2rem",
        backgroundColor: alpha(theme.palette.background.paper, 0.85), // was translucent white — a light box on dark rows
    },
    "&:hover .copy-icon, &:focus .copy-icon": {
        opacity: 1,
        visibility: "visible",
        color: theme.palette.cobaltBlue?.hover,
    },
}));

const StyledPopoverText = styled("span")(({ theme }) => ({
    color: theme.palette.softBlack?.main,
    fontSize: ".68rem",
    lineHeight: "1.25rem",
}));

const TIMEOUT_DELAY = 1500;

interface IProps {
    children?: any;
    wrapper?: boolean;
    autoCalcRightPosition?: boolean;
    rightOffset?: CSSProperties["right"];
}

const CopyToClipboard: FC<IProps> = ({ children, rightOffset, wrapper = false, autoCalcRightPosition = true }) => {
    const popoverAnchor = useRef<HTMLSpanElement | null>(null);

    const [openedPopover, setOpenedPopover] = useState(false);
    const [offset, setOffset] = useState<CSSProperties["right"]>(rightOffset || "-20px");
    const theme = useTheme();
    const handleClick = (event) => {
        // When the icon is clicked we do not want to trigger the default action of the element
        // i.e. opening a link
        event.preventDefault();
        event.stopPropagation();
        // debugger;
        // Copy only if clicked within the icon area
        copyToClipboard(event.currentTarget.parentNode.textContent)
            .then(() => {
                setOpenedPopover(true);
                debounceFunc(() => setOpenedPopover(false), TIMEOUT_DELAY)();
            })
            .catch((_error) => toastError(<FormattedMessage id="tenant-settings.message.api-keys-copy-failed" defaultMessage="Copy to clipboard failed!" />));
    };

    const onMouseOver = () => {
        if (!popoverAnchor.current?.parentElement?.parentElement || !autoCalcRightPosition) {
            return;
        }

        const target = popoverAnchor.current.parentElement.parentElement;

        if (target.scrollWidth > target.clientWidth) {
            setOffset(`calc(${rightOffset || "-20px"} + ${target.scrollWidth - target.clientWidth}px)`);
        } else {
            setOffset(rightOffset);
        }
    };

    return (
        <>
            <StyledSpan
                onMouseOver={onMouseOver}
                ref={popoverAnchor}
                aria-owns={openedPopover ? "clipboard-popover" : undefined}
                aria-haspopup="true"
                rightOffset={offset}
            >
                {wrapper ? (
                    <div>
                        {children}
                        <ContentCopyIcon className="copy-icon" onClick={handleClick} />
                    </div>
                ) : (
                    <>
                        {children}
                        <ContentCopyIcon className="copy-icon" onClick={handleClick} />
                    </>
                )}
            </StyledSpan>
            <Popover
                id="clipboard-popover"
                open={openedPopover}
                anchorEl={() => popoverAnchor.current!}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "center" }}
                slotProps={{
                    root: { style: { pointerEvents: "none" } },
                    paper: {
                        style: {
                            pointerEvents: "auto",
                            background: theme.palette.background.paper,
                            boxShadow: "0px 3px 9px rgba(0, 0, 0, 0.12)",
                            borderRadius: "4px",
                        },
                    },
                }}
            >
                <PopoverWrapper>
                    <DoneIcon style={{ color: theme.palette.softBlack?.main, fontSize: "0.85rem", textAlign: "center" }} />
                    <StyledPopoverText>
                        <FormattedMessage id="tenant-settings.label.api-keys-popover-text" defaultMessage="Copied to clipboard" />
                    </StyledPopoverText>
                </PopoverWrapper>
            </Popover>
        </>
    );
};

export default CopyToClipboard;
